import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Input,
  Select,
  HStack,
  Button,
  Flex,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Icon,
  InputGroup,
  InputLeftElement,
  Badge,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Divider,
  Checkbox,
  useToast
} from '@chakra-ui/react';

import { 
  ChevronUpIcon, 
  ChevronDownIcon, 
} from '@chakra-ui/icons';

import { 
  MdFilterList, 
  MdRefresh, 
  MdSearch, 
  MdDownload, 
  MdClear, 
  MdAttachMoney,
} from 'react-icons/md';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const PrecatoriosAC = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [selectedPrecatorios, setSelectedPrecatorios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'valor_atualizado', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterNatureza, setFilterNatureza] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [currentPriceRange, setCurrentPriceRange] = useState([0, 10000000]);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  
  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const highlightColor = useColorModeValue('blue.600', 'blue.400');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    const fetchPrecatorios = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/precatorioAC`);
        const data = await res.json();
        setPrecatorios(data);
        
        // Initialize price range based on data
        if (data.length > 0) {
          const values = data.map(p => p.valor_atualizado || 0);
          const minValue = Math.max(0, Math.floor(Math.min(...values)));
          const maxValue = Math.ceil(Math.max(...values));
          setPriceRange([minValue, maxValue]);
          setCurrentPriceRange([minValue, maxValue]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar precatórios:', error);
        setErro(true);
        setLoading(false);
      }
    };

    fetchPrecatorios();
  }, []);

  const SortIcon = ({ isActive, direction }) => {
    return direction === 'asc' ? (
      <ChevronUpIcon ml={2} opacity={isActive ? 1 : 0.3} />
    ) : (
      <ChevronDownIcon ml={2} opacity={isActive ? 1 : 0.3} />
    );
  };

  // Extract unique filters from data
  const naturezas = useMemo(() => [...new Set(precatorios.map(p => p.natureza).filter(Boolean))], [precatorios]);
  const entes = useMemo(() => [...new Set(precatorios.map(p => p.ente).filter(Boolean))], [precatorios]);

  const filteredData = useMemo(() => {
    let result = [...precatorios];
  
    // Search + Filters
    result = result.filter(p =>
      p.numero_precatorio?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (!filterNatureza || p.natureza === filterNatureza) &&
      (!filterEnte || p.ente === filterEnte) &&
      (p.valor_atualizado || 0) >= currentPriceRange[0] &&
      (p.valor_atualizado || 0) <= currentPriceRange[1]
    );
  
    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] ?? '';
        const bVal = b[sortConfig.key] ?? '';
        
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        
        if (sortConfig.direction === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }
  
    return result;
  }, [precatorios, searchTerm, sortConfig, filterNatureza, filterEnte, currentPriceRange]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleSelectPrecatorio = (precatorioId) => {
    setSelectedPrecatorios(prev => {
      if (prev.includes(precatorioId)) {
        return prev.filter(id => id !== precatorioId);
      } else {
        return [...prev, precatorioId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPrecatorios.length === paginatedData.length) {
      setSelectedPrecatorios([]);
    } else {
      setSelectedPrecatorios(paginatedData.map(p => p._id));
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterNatureza('');
    setFilterEnte('');
    setCurrentPriceRange(priceRange);
    setCurrentPage(1);
  };

  const totalValue = filteredData.reduce((sum, p) => sum + (p.valor_atualizado || 0), 0);
  const selectedValue = selectedPrecatorios.reduce((sum, id) => {
    const precatorio = precatorios.find(p => p._id === id);
    return sum + (precatorio?.valor_atualizado || 0);
  }, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Carregando precatórios do Acre...</Text>
        </VStack>
      </Box>
    );
  }

  if (erro) {
    return (
      <Alert status="error">
        <AlertIcon />
        Erro ao carregar os dados dos precatórios do Acre. Tente novamente mais tarde.
      </Alert>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" color={headingColor} mb={2}>
            Precatórios - Acre (AC)
          </Heading>
          <Text color={textColor}>
            Gerencie os precatórios do Estado do Acre
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Total de Precatórios</StatLabel>
                <StatNumber>{filteredData.length}</StatNumber>
                <StatHelpText>
                  {precatorios.length} no total
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Valor Total</StatLabel>
                <StatNumber fontSize="lg">{formatCurrency(totalValue)}</StatNumber>
                <StatHelpText>
                  Valor filtrado
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Selecionados</StatLabel>
                <StatNumber>{selectedPrecatorios.length}</StatNumber>
                <StatHelpText>
                  {formatCurrency(selectedValue)}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Páginas</StatLabel>
                <StatNumber>{currentPage} de {totalPages}</StatNumber>
                <StatHelpText>
                  {itemsPerPage} por página
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Controls */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">Filtros e Controles</Heading>
              <HStack>
                <Button
                  leftIcon={<Icon as={MdFilterList} />}
                  onClick={onOpen}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                >
                  Filtros Avançados
                </Button>
                <Button
                  leftIcon={<Icon as={MdClear} />}
                  onClick={clearFilters}
                  variant="ghost"
                  size="sm"
                >
                  Limpar
                </Button>
                <Button
                  leftIcon={<Icon as={MdRefresh} />}
                  onClick={() => window.location.reload()}
                  variant="ghost"
                  size="sm"
                >
                  Atualizar
                </Button>
              </HStack>
            </HStack>
          </CardHeader>
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={MdSearch} color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por número do precatório..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Select
                placeholder="Todas as naturezas"
                value={filterNatureza}
                onChange={(e) => setFilterNatureza(e.target.value)}
                maxW="200px"
              >
                {naturezas.map(natureza => (
                  <option key={natureza} value={natureza}>{natureza}</option>
                ))}
              </Select>

              <Select
                placeholder="Todos os entes"
                value={filterEnte}
                onChange={(e) => setFilterEnte(e.target.value)}
                maxW="200px"
              >
                {entes.map(ente => (
                  <option key={ente} value={ente}>{ente}</option>
                ))}
              </Select>

              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                maxW="120px"
              >
                {ITEMS_PER_PAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option} por página</option>
                ))}
              </Select>
            </HStack>
          </CardBody>
        </Card>

        {/* Table */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody p={0}>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>
                      <Checkbox
                        isChecked={selectedPrecatorios.length === paginatedData.length && paginatedData.length > 0}
                        isIndeterminate={selectedPrecatorios.length > 0 && selectedPrecatorios.length < paginatedData.length}
                        onChange={handleSelectAll}
                      />
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('ordem')}>
                      <HStack>
                        <Text>Ordem</Text>
                        <SortIcon isActive={sortConfig.key === 'ordem'} direction={sortConfig.direction} />
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('numero_precatorio')}>
                      <HStack>
                        <Text>Número</Text>
                        <SortIcon isActive={sortConfig.key === 'numero_precatorio'} direction={sortConfig.direction} />
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('natureza')}>
                      <HStack>
                        <Text>Natureza</Text>
                        <SortIcon isActive={sortConfig.key === 'natureza'} direction={sortConfig.direction} />
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('valor_atualizado')}>
                      <HStack>
                        <Text>Valor</Text>
                        <SortIcon isActive={sortConfig.key === 'valor_atualizado'} direction={sortConfig.direction} />
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('ente')}>
                      <HStack>
                        <Text>Ente</Text>
                        <SortIcon isActive={sortConfig.key === 'ente'} direction={sortConfig.direction} />
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('status')}>
                      <HStack>
                        <Text>Status</Text>
                        <SortIcon isActive={sortConfig.key === 'status'} direction={sortConfig.direction} />
                      </HStack>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedData.map((precatorio) => (
                    <Tr 
                      key={precatorio._id} 
                      _hover={{ bg: hoverBg }}
                      bg={selectedPrecatorios.includes(precatorio._id) ? 'blue.50' : 'transparent'}
                    >
                      <Td>
                        <Checkbox
                          isChecked={selectedPrecatorios.includes(precatorio._id)}
                          onChange={() => handleSelectPrecatorio(precatorio._id)}
                        />
                      </Td>
                      <Td>
                        <Badge colorScheme="gray">{precatorio.ordem}</Badge>
                      </Td>
                      <Td>
                        <Text fontFamily="mono" fontSize="sm">
                          {precatorio.numero_precatorio}
                        </Text>
                      </Td>
                      <Td>
                        <Text noOfLines={2}>{precatorio.natureza}</Text>
                      </Td>
                      <Td>
                        <Text fontWeight="semibold" color={highlightColor}>
                          {formatCurrency(precatorio.valor_atualizado)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{precatorio.ente}</Text>
                      </Td>
                      <Td>
                        <Badge 
                          colorScheme={
                            precatorio.status === 'Vendida' ? 'green' :
                            precatorio.status === 'À Venda' ? 'blue' :
                            precatorio.status === 'Em Análise' ? 'yellow' :
                            'gray'
                          }
                        >
                          {precatorio.status || 'Não Contatou'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>

        {/* Pagination */}
        <HStack justify="space-between" align="center">
          <Text color={textColor}>
            Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, filteredData.length)} a{' '}
            {Math.min(currentPage * itemsPerPage, filteredData.length)} de {filteredData.length} resultados
          </Text>
          <HStack>
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
            >
              Anterior
            </Button>
            <Text>{currentPage} de {totalPages}</Text>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
            >
              Próxima
            </Button>
          </HStack>
        </HStack>
      </VStack>

      {/* Advanced Filters Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Filtros Avançados</DrawerHeader>

          <DrawerBody>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Faixa de Valores</FormLabel>
                <Text fontSize="sm" color={textColor} mb={4}>
                  {formatCurrency(currentPriceRange[0])} - {formatCurrency(currentPriceRange[1])}
                </Text>
                <RangeSlider
                  value={currentPriceRange}
                  onChange={setCurrentPriceRange}
                  min={priceRange[0]}
                  max={priceRange[1]}
                  step={1000}
                >
                  <RangeSliderTrack>
                    <RangeSliderFilledTrack />
                  </RangeSliderTrack>
                  <RangeSliderThumb index={0} />
                  <RangeSliderThumb index={1} />
                </RangeSlider>
              </FormControl>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={2}>Filtros Aplicados</Text>
                <VStack align="stretch" spacing={2}>
                  {searchTerm && (
                    <Badge colorScheme="blue">Busca: {searchTerm}</Badge>
                  )}
                  {filterNatureza && (
                    <Badge colorScheme="green">Natureza: {filterNatureza}</Badge>
                  )}
                  {filterEnte && (
                    <Badge colorScheme="purple">Ente: {filterEnte}</Badge>
                  )}
                </VStack>
              </Box>
            </VStack>
          </DrawerBody>

          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={onClose}>
              Aplicar Filtros
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default PrecatoriosAC;